/**
 * CARROUSEL SORTIES JEUX VIDÉOS - JAVASCRIPT
 * Gestion de la navigation et du swipe
 */

class CarrousselSortiesJeuxVideos {
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.carroussel-sorties-track');
    this.prevButton = container.querySelector('.carroussel-nav-prev');
    this.nextButton = container.querySelector('.carroussel-nav-next');
    this.items = container.querySelectorAll('.carroussel-sorties-item');
    
    // Variables pour le swipe
    this.isDragging = false;
    this.startPos = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;
    this.currentIndex = 0;

    this.init();
  }

  init() {
    if (!this.track || this.items.length === 0) return;

    // Trier les items par date timestamp (du plus proche au plus lointain)
    this.sortItemsByDate();

    // Calculer le nombre d'items visibles selon la largeur d'écran
    this.updateVisibleItems();
    this.updateNavigationButtons();
    
    // Centrer les flèches sur les images
    this.centerNavigationButtons();

    // Navigation par boutons
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.scrollPrev());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.scrollNext());
    }

    // Navigation tactile et souris
    this.setupTouchEvents();

    // Réajuster lors du redimensionnement
    window.addEventListener('resize', () => {
      this.updateVisibleItems();
      this.updateNavigationButtons();
      this.centerNavigationButtons();
    });

    // Mise à jour des boutons lors du scroll
    this.track.addEventListener('scroll', () => {
      this.updateNavigationButtons();
    });
  }

  sortItemsByDate() {
    // Convertir NodeList en Array et trier
    const itemsArray = Array.from(this.items);
    
    itemsArray.sort((a, b) => {
      const timestampA = parseInt(a.getAttribute('data-date-timestamp')) || 0;
      const timestampB = parseInt(b.getAttribute('data-date-timestamp')) || 0;
      return timestampA - timestampB; // Tri croissant (plus proche en premier)
    });

    // Réorganiser les éléments dans le DOM
    itemsArray.forEach(item => {
      this.track.appendChild(item);
    });

    // Remettre le scroll au début
    this.track.scrollLeft = 0;

    // Mettre à jour la référence aux items
    this.items = this.container.querySelectorAll('.carroussel-sorties-item');
  }

  updateVisibleItems() {
    const containerWidth = this.track.offsetWidth;
    const itemWidth = this.items[0]?.offsetWidth || 0;
    
    if (itemWidth > 0) {
      this.itemsPerView = Math.floor(containerWidth / itemWidth);
    } else {
      // Fallback basé sur la largeur d'écran
      if (window.innerWidth <= 768) {
        this.itemsPerView = 2; // Mobile
      } else {
        this.itemsPerView = 4; // Desktop et Tablette
      }
    }
  }

  updateNavigationButtons() {
    if (!this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const currentScroll = this.track.scrollLeft;

    // Masquer/afficher les boutons selon la position
    if (currentScroll <= 0) {
      this.prevButton.classList.add('hidden');
    } else {
      this.prevButton.classList.remove('hidden');
    }

    if (currentScroll >= maxScroll - 5) { // -5 pour la tolérance
      this.nextButton.classList.add('hidden');
    } else {
      this.nextButton.classList.remove('hidden');
    }

    // Masquer les boutons si tous les items sont visibles
    if (this.items.length <= this.itemsPerView) {
      this.prevButton.classList.add('hidden');
      this.nextButton.classList.add('hidden');
    }
  }

  centerNavigationButtons() {
    if (!this.prevButton || !this.nextButton || this.items.length === 0) return;

    // Récupérer la hauteur de la première image
    const firstImage = this.items[0].querySelector('.carroussel-sorties-image');
    if (!firstImage) return;

    const imageHeight = firstImage.offsetHeight;
    const centerPosition = imageHeight / 2;

    // Appliquer la position calculée aux deux boutons
    this.prevButton.style.top = `${centerPosition}px`;
    this.nextButton.style.top = `${centerPosition}px`;
  }

  scrollPrev() {
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;
    const scrollAmount = (itemWidth + gap) * this.itemsPerView;
    
    this.track.scrollBy({
      left: -scrollAmount,
      behavior: 'smooth'
    });
  }

  scrollNext() {
    const itemWidth = this.items[0]?.offsetWidth || 0;
    const gap = parseInt(getComputedStyle(this.track).gap) || 20;
    const scrollAmount = (itemWidth + gap) * this.itemsPerView;
    
    this.track.scrollBy({
      left: scrollAmount,
      behavior: 'smooth'
    });
  }

  setupTouchEvents() {
    // Touch events pour mobile
    this.track.addEventListener('touchstart', this.touchStart.bind(this), { passive: true });
    this.track.addEventListener('touchmove', this.touchMove.bind(this), { passive: true });
    this.track.addEventListener('touchend', this.touchEnd.bind(this));

    // Mouse events pour desktop
    this.track.addEventListener('mousedown', this.touchStart.bind(this));
    this.track.addEventListener('mousemove', this.touchMove.bind(this));
    this.track.addEventListener('mouseup', this.touchEnd.bind(this));
    this.track.addEventListener('mouseleave', this.touchEnd.bind(this));

    // Empêcher le drag des images
    this.track.querySelectorAll('img').forEach(img => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  touchStart(event) {
    this.isDragging = true;
    this.startPos = this.getPositionX(event);
    this.prevTranslate = this.track.scrollLeft;
    this.track.style.cursor = 'grabbing';
    this.track.style.scrollBehavior = 'auto';
  }

  touchMove(event) {
    if (!this.isDragging) return;
    
    const currentPosition = this.getPositionX(event);
    const diff = this.startPos - currentPosition;
    this.track.scrollLeft = this.prevTranslate + diff;
  }

  touchEnd() {
    this.isDragging = false;
    this.track.style.cursor = 'grab';
    this.track.style.scrollBehavior = 'smooth';
  }

  getPositionX(event) {
    return event.type.includes('mouse') ? event.pageX : event.touches[0].clientX;
  }
}

// Initialisation au chargement du DOM
document.addEventListener('DOMContentLoaded', () => {
  const carrousselContainers = document.querySelectorAll('.carroussel-sorties-container');
  
  carrousselContainers.forEach(container => {
    new CarrousselSortiesJeuxVideos(container);
  });
});

// Support pour le theme editor de Shopify
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const container = event.target.querySelector('.carroussel-sorties-container');
    if (container) {
      new CarrousselSortiesJeuxVideos(container);
    }
  });
}

